import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/map';
import { Card } from '../../app/models/Card';
import { CardStatement } from '../../app/models/CardStatement';
import { CardTransaction } from '../../app/models/CardTransaction';
import { CardType } from '../../app/models/CardType';
import { Coupon } from '../../app/models/Coupon';
import { CouponTransaction } from '../../app/models/CouponTransaction';
import { CouponType } from '../../app/models/CouponType';
import { CurrentAccount } from '../../app/models/CurrentAccount';
import { Customer } from '../../app/models/Customer';
import { CustomerGroup } from '../../app/models/CustomerGroup';
import { DailyCouponList } from '../../app/models/DailyCouponList';
import { FileModel } from '../../app/models/FileModel';
import { LookalikeCardCreation } from '../../app/models/LookalikeCardCreation';
import { Organization } from '../../app/models/Organization';
import { PoS } from '../../app/models/PoS';
import { PosAppVersion } from '../../app/models/PosAppVersion';
import { PricelistProduct } from '../../app/models/PriceListProduct';
import { PriceList } from '../../app/models/Pricelist';
import { Product } from '../../app/models/Product';
import { ProductCategory } from '../../app/models/ProductCategory';
import { RegisterActivity } from '../../app/models/RegisterActivity';
import { SpecialOffer } from '../../app/models/SpecialOffer';
import { TaxRate } from '../../app/models/TaxRate';
import { UnitOfMeasure } from '../../app/models/UnitOfMeasure';
import { User } from '../../app/models/User';
import { Zone } from '../../app/models/Zone';
import { AuthenticationService } from '../authentication/authenticationService';
import { ConfigService } from '../config/config.service';
import { GenericQueryParams } from '../../utils/utils';
import { UserService } from '../../services/user.service';
import { CommonService, GenericPaginatedDataType } from '../../services/common.service';

export type GetCustomersType = GenericPaginatedDataType<Customer>;
export type GetCardsType = GenericPaginatedDataType<Card>;
export type GetCurrentAccountsType = GenericPaginatedDataType<CurrentAccount>;
export type GetRegisterActivitiesType = GenericPaginatedDataType<RegisterActivity>;

@Injectable()
export class PaytronBackendService {
  private users: Subject<User[]>;
  private customers: Subject<Customer[]>;
  private customerGroups: Subject<CustomerGroup[]>;
  private cards: Subject<Card[]>;

  constructor(
    private commonService: CommonService,
    private http: HttpClient,
    private authenticationService: AuthenticationService,
    private configService: ConfigService,
    private userService: UserService
  ) {
    this.users = new Subject<any>();
    this.users.next([]);
    this.customers = new Subject<any>();
    this.customers.next([]);
    this.customerGroups = new Subject<any>();
    this.customerGroups.next([]);
    this.cards = new Subject<any>();
    this.cards.next([]);
  }

  addCardTransaction(transaction: CardTransaction, endpoint: String): Observable<CardTransaction> {
    let url = this.configService.getUrl() + endpoint;
    type typeToReturn = CardTransaction;
    let response = this.commonService.createData<typeToReturn>(url, transaction);
    return response;
  }

  addRechargeTransaction(transaction: CardTransaction): Observable<CardTransaction> {
    return this.addCardTransaction(transaction, 'cardtransactions/recharges');
  }

  addPaymentTransaction(transaction: CardTransaction): Observable<CardTransaction> {
    return this.addCardTransaction(transaction, 'cardtransactions/payments');
  }

  addSecurityDepositTransaction(transaction: CardTransaction): Observable<CardTransaction> {
    return this.addCardTransaction(transaction, 'cardtransactions/securitydeposits');
  }

  addReversalTransaction(transaction: CardTransaction): Observable<CardTransaction> {
    return this.addCardTransaction(transaction, 'cardtransactions/reversals');
  }

  addDischargeTransaction(transaction: CardTransaction): Observable<CardTransaction> {
    return this.addCardTransaction(transaction, 'cardtransactions/discharges?force=' + transaction.force);
  }

  giveBackAllDepositsByCard(
    card: Card,
    balanceSnapshot: number,
    nextBalanceVersion: number,
    pos: PoS
  ): Observable<CardTransaction[]> {
    let url =
      this.configService.getUrl() +
      'cardtransactions/cards/' +
      card.uuid +
      '/givebacks?balanceSnapshot=' +
      balanceSnapshot +
      '&&nextBalanceVersion=' +
      nextBalanceVersion +
      '&&posUuid=' +
      pos.uuid;
    type typeToReturn = CardTransaction[];
    let response = this.commonService.createData<typeToReturn>(url, pos);
    return response;
  }

  getCardTransactionsByCard(card: Card): Observable<CardTransaction[]> {
    let url = this.configService.getUrl() + 'cards/' + card.uuid + '/cardtransactions';
    type typeToReturn = CardTransaction[];
    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  getCardStatements(cardUuid: string): Observable<CardStatement> {
    let url = this.configService.getUrl() + 'cards/' + cardUuid + '/statements';
    type typeToReturn = CardStatement;
    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  saveOrUpdateCard(card: Card): Observable<Card> {
    let edit = card.uuid != null;
    return edit ? this.editCard(card) : this.addCard(card);
  }

  private addCard(card: Card): Observable<Card> {
    let url = this.configService.getUrl() + 'cards';
    type typeToReturn = Card;
    let response = this.commonService.createData<typeToReturn>(url, card);
    return response;
  }

  private editCard(card: Card): Observable<Card> {
    let url = this.configService.getUrl() + 'cards/' + card.uuid;
    type typeToReturn = Card;
    let response = this.commonService.editData<typeToReturn>(url, card);
    return response;
  }

  getCard(uuid: String, checkForMissingTransactions?: boolean): Observable<Card> {
    let url = this.configService.getUrl() + 'cards/' + uuid;
    if (checkForMissingTransactions) {
      url += '?checkForMissingTransactions=true';
    }
    type typeToReturn = Card;

    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  getCards() {
    const url = this.configService.getUrl() + 'cards/';
    return this.http.get<Card[]>(url, {
      ...this.commonService.getHttpOptions(),
    });
  }
  getPaginatedCards(params: GenericQueryParams): Observable<GetCardsType> {
    return this.commonService.getPaginatedQuery<GetCardsType>('cards', params);
  }

  createLookalikeCard(
    sourceCardUuid: string,
    lookalikeCard: { posUuid: string; activationDate: string }
  ): Observable<LookalikeCardCreation> {
    let url = this.configService.getUrl() + 'cards/' + sourceCardUuid + '/lookalike';
    type typeToReturn = LookalikeCardCreation;
    let response = this.commonService.createData<typeToReturn>(url, lookalikeCard);
    return response;
  }

  saveOrUpdateSpecialOffer(specialOffer: SpecialOffer): Observable<SpecialOffer> {
    let edit = specialOffer.uuid != null;
    return edit ? this.editSpecialOffer(specialOffer) : this.addSpecialOffer(specialOffer);
  }

  private addSpecialOffer(specialOffer: SpecialOffer): Observable<SpecialOffer> {
    let url = this.configService.getUrl() + 'specialoffers/';
    type typeToReturn = SpecialOffer;
    let response = this.commonService.createData<typeToReturn>(url, specialOffer);
    return response;
  }

  private editSpecialOffer(specialOffer: SpecialOffer): Observable<SpecialOffer> {
    let url = this.configService.getUrl() + 'specialoffers/' + specialOffer.uuid;
    type typeToReturn = SpecialOffer;
    let response = this.commonService.editData<typeToReturn>(url, specialOffer);
    return response;
  }

  getSpecialOffers(): Observable<SpecialOffer[]> {
    let url = this.configService.getUrl() + 'specialoffers/';
    type typeToReturn = SpecialOffer[];

    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  deleteSpecialOffer(specialOffer: SpecialOffer) {
    let url = this.configService.getUrl() + 'specialoffers/' + specialOffer.uuid;
    type typeToReturn = SpecialOffer;
    let response = this.commonService.deleteData<typeToReturn>(url);
    return response;
  }

  getRegisterActivities(params?: GenericQueryParams): Observable<GetRegisterActivitiesType> {
    return this.commonService.getPaginatedQuery<GetRegisterActivitiesType>('posregisteractivities', params);
  }

  getRegisterActivity(registerActivityUuid: string): Observable<RegisterActivity> {
    let url = this.configService.getUrl() + 'posregisteractivities/' + registerActivityUuid;
    type typeToReturn = RegisterActivity;

    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  getLastRegisterActivity(posUuid: string): Observable<RegisterActivity> {
    let url = this.configService.getUrl() + 'posregisteractivities/last?posUuid=' + posUuid;
    type typeToReturn = RegisterActivity;
    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  calculateRegisterActivity(registerActivity: RegisterActivity): Observable<RegisterActivity> {
    let url = this.configService.getUrl() + 'posregisteractivities/' + registerActivity.uuid + '/calculate';
    type typeToReturn = RegisterActivity;
    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  openRegisterActivity(pos: PoS): Observable<RegisterActivity> {
    let url = this.configService.getUrl() + 'posregisteractivities/open?posUuid=' + pos.uuid;
    type typeToReturn = RegisterActivity;
    let response = this.commonService.createData<typeToReturn>(url, null);
    return response;
  }

  closeRegisterActivity(registerActivityUuid: string): Observable<RegisterActivity> {
    let url = this.configService.getUrl() + 'posregisteractivities/' + registerActivityUuid + '/close';
    type typeToReturn = RegisterActivity;
    let response = this.commonService.editData<typeToReturn>(url, null);
    return response;
  }

  getRegisterActivityTransactions(registerActivityUuid: string): Observable<CardTransaction[]> {
    let url = this.configService.getUrl() + 'posregisteractivities/' + registerActivityUuid + '/cardtransactions';
    type typeToReturn = CardTransaction[];
    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  getCardTypes(service: boolean = null, includeAdditionalCircuits: boolean = null): Observable<CardType[]> {
    let url = this.configService.getUrl() + 'cardtypes/';
    const queryParams: string[] = [];
    if (service != null) {
      queryParams.push('service=' + service);
    }
    if (includeAdditionalCircuits != null) {
      queryParams.push('includeAdditionalCircuits=' + includeAdditionalCircuits);
    }
    url = queryParams.reduce(
      (acc: string, val: string, index: number) => acc + (index === 0 ? '?' + val : '&' + val),
      url
    );
    type typeToReturn = CardType[];

    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  editCardType(cardType: CardType): Observable<CardType> {
    let url = this.configService.getUrl() + 'cardtypes/' + cardType.uuid;
    type typeToReturn = CardType;
    let response = this.commonService.editData<typeToReturn>(url, cardType);
    return response;
  }

  saveOrUpdateCurrentAccount(currentAccount: CurrentAccount): Observable<CurrentAccount> {
    let edit = currentAccount.uuid != null;
    return edit ? this.editCurrentAccount(currentAccount) : this.addCurrentAccount(currentAccount);
  }

  private addCurrentAccount(currentAccount: CurrentAccount): Observable<CurrentAccount> {
    let url = this.configService.getUrl() + 'currentaccounts';
    type typeToReturn = CurrentAccount;
    let response = this.commonService.createData<typeToReturn>(url, currentAccount);

    return response;
  }

  private editCurrentAccount(currentAccount: CurrentAccount): Observable<CurrentAccount> {
    let url = this.configService.getUrl() + 'currentaccounts/' + currentAccount.uuid;
    type typeToReturn = CurrentAccount;
    let response = this.commonService.editData<typeToReturn>(url, currentAccount);
    return response;
  }

  checkOut(currentAccount: CurrentAccount, posId: String): Observable<CurrentAccount> {
    let url =
      this.configService.getUrl() +
      'currentaccounts/' +
      currentAccount.uuid +
      '/checkout?partial=false&&posUuid=' +
      posId;
    type typeToReturn = CurrentAccount;
    let response = this.commonService.editData<typeToReturn>(url, currentAccount);
    return response;
  }

  partialCheckOut(currentAccount: CurrentAccount, posId: String): Observable<CurrentAccount> {
    let url =
      this.configService.getUrl() +
      'currentaccounts/' +
      currentAccount.uuid +
      '/checkout?partial=true&&posUuid=' +
      posId;
    type typeToReturn = CurrentAccount;
    let response = this.commonService.editData<typeToReturn>(url, currentAccount);
    return response;
  }

  saveOrUpdateCustomer(customer: Customer): Observable<Customer> {
    let edit = customer.uuid != null;
    return edit ? this.editCustomer(customer) : this.addCustomer(customer);
  }

  private addCustomer(customer: Customer): Observable<Customer> {
    let url = this.configService.getUrl() + 'customers';
    type typeToReturn = Customer;
    let response = this.commonService.createData<typeToReturn>(url, customer);

    return response;
  }

  private editCustomer(customer: Customer): Observable<Customer> {
    let url = this.configService.getUrl() + 'customers/' + customer.uuid;
    type typeToReturn = Customer; //Observable<HttpResponse<Customer>>;
    let response = this.commonService.editData<typeToReturn>(url, customer);
    return response;
  }

  getCustomer(uuid: String): Observable<Customer> {
    let url = this.configService.getUrl() + 'customers/' + uuid;
    type typeToReturn = Customer;

    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }
  getCustomers() {
    const url = this.configService.getUrl() + 'customers/';
    return this.http.get<Customer[]>(url, {
      ...this.commonService.getHttpOptions(),
    });
  }
  getPaginatedCustomers(params: GenericQueryParams): Observable<GetCustomersType> {
    return this.commonService.getPaginatedQuery<GetCustomersType>('customers', params);
  }

  getCustomerByCurrentAccount(currentAccount: CurrentAccount) {
    let url = this.configService.getUrl() + 'currentaccounts/' + currentAccount.uuid + '/customer';
    type typeToReturn = Customer;

    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  getPaginatedCurrentAccounts(params: GenericQueryParams): Observable<GenericPaginatedDataType<CurrentAccount>> {
    return this.commonService.getPaginatedQuery<GetCurrentAccountsType>('currentaccounts', params);
  }

  getCurrentAccount(uuid: String): Observable<CurrentAccount> {
    let url = this.configService.getUrl() + 'currentaccounts/' + uuid;
    type typeToReturn = CurrentAccount;

    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  getCurrentAccountByCard(card: Card) {
    let url = this.configService.getUrl() + 'cards/' + card.uuid + '/currentaccount';
    type typeToReturn = CurrentAccount;

    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  getAccountOwnerByCard(card: Card) {
    let url = this.configService.getUrl() + 'cards/' + card.uuid;
    url += card.type.service ? '/user' : '/customer';
    type typeToReturn = User | Customer;
    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  getOwnerByCurrentAccount(currentAccount: CurrentAccount) {
    switch (currentAccount.accountHolderType.toString()) {
      case 'Customer':
        return this.getCustomerByCurrentAccount(currentAccount);
      case 'User':
        return this.userService.getUserByCurrentAccount(currentAccount);
      default:
        console.error('Wrong AccountHolder value.');
        break;
    }
  }

  deleteCustomer(customer: Customer) {
    let url = this.configService.getUrl() + 'customers/' + customer.uuid;
    type typeToReturn = Customer;
    let response = this.commonService.deleteData<typeToReturn>(url);
    return response;
  }

  saveOrUpdateUser(user: User): Observable<User> {
    let edit = user.uuid != null;
    return edit ? this.editUser(user) : this.addUser(user);
  }

  private addUser(user: User): Observable<User> {
    let url = this.configService.getUrl() + 'users';
    type typeToReturn = User;
    let response = this.commonService.createData<typeToReturn>(url, user);
    return response;
  }

  private editUser(user: User): Observable<User> {
    let url = this.configService.getUrl() + 'users/' + user.uuid;
    type typeToReturn = User;
    let response = this.commonService.editData<typeToReturn>(url, user);
    return response;
  }

  resetPassword(user: User): Observable<User> {
    let url = this.configService.getUrl() + 'users/' + user.uuid + '/resetpassword';
    type typeToReturn = User;
    let response = this.commonService.createData<typeToReturn>(url, user);
    return response;
  }

  deleteUser(user: User) {
    let url = this.configService.getUrl() + 'users/' + user.uuid;
    type typeToReturn = Observable<HttpResponse<User>>;
    let response = this.commonService.deleteData<typeToReturn>(url);
    return response;
  }

  isSetupUserAvailable(): Observable<boolean> {
    let url = this.configService.getUrl() + 'users/issetupuseravailable';
    type typeToReturn = boolean;
    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  saveOrUpdateCustomerGroup(customerGroup: CustomerGroup): Observable<CustomerGroup> {
    let edit = customerGroup.uuid != null;
    return edit ? this.editCustomerGroup(customerGroup) : this.addCustomerGroup(customerGroup);
  }

  private addCustomerGroup(customerGroup: CustomerGroup): Observable<CustomerGroup> {
    let url = this.configService.getUrl() + 'customergroups';
    type typeToReturn = CustomerGroup;
    let response = this.commonService.createData<typeToReturn>(url, customerGroup);
    return response;
  }

  private editCustomerGroup(customerGroup: CustomerGroup): Observable<CustomerGroup> {
    let url = this.configService.getUrl() + 'customergroups/' + customerGroup.uuid;
    type typeToReturn = CustomerGroup; //Observable<HttpResponse<CustomerGroup>>;
    let response = this.commonService.editData<typeToReturn>(url, customerGroup);
    return response;
  }

  getCustomerGroup(uuid: String): Observable<CustomerGroup> {
    let url = this.configService.getUrl() + 'customergroups/' + uuid;
    type typeToReturn = CustomerGroup;

    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  getCustomerGroups(): Observable<CustomerGroup[]> {
    let url = this.configService.getUrl() + 'customergroups';
    type typeToReturn = CustomerGroup[];

    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  deleteCustomerGroup(customerGroup: CustomerGroup) {
    let url = this.configService.getUrl() + 'customergroups/' + customerGroup.uuid;
    type typeToReturn = CustomerGroup;
    let response = this.commonService.deleteData<typeToReturn>(url);
    return response;
  }

  saveOrUpdatePos(pos: PoS): Observable<PoS> {
    let edit = pos.uuid != null;
    return edit ? this.editPos(pos) : this.addPos(pos);
  }

  private addPos(pos: PoS): Observable<PoS> {
    let url = this.configService.getUrl() + 'pointsofsale/';
    type typeToReturn = PoS;
    let response = this.commonService.createData<typeToReturn>(url, pos);
    return response;
  }

  private editPos(pos: PoS): Observable<PoS> {
    let url = this.configService.getUrl() + 'pointsofsale/' + pos.uuid;
    type typeToReturn = PoS;
    let response = this.commonService.editData<typeToReturn>(url, pos);
    return response;
  }

  deletePos(pos: PoS) {
    let url = this.configService.getUrl() + 'pointsofsale/' + pos.uuid;
    type typeToReturn = PoS;
    let response = this.commonService.deleteData<typeToReturn>(url);
    return response;
  }

  getPoSInfo(codeId: string): Observable<PoS> {
    let url = this.configService.getUrl() + 'pointsofsale/code/' + codeId;
    type typeToReturn = PoS;
    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  getPoSList(): Observable<PoS[]> {
    let url = this.configService.getUrl() + 'pointsofsale/';
    type typeToReturn = PoS[];
    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  getClosedPoSList(): Observable<PoS[]> {
    let url = this.configService.getUrl() + 'pointsofsale/closed';
    type typeToReturn = PoS[];
    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  getTaxRates(): Observable<TaxRate[]> {
    let url = this.configService.getUrl() + 'taxrates/';
    type typeToReturn = TaxRate[];
    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  saveOrUpdateTaxRate(taxrate: TaxRate): Observable<TaxRate> {
    let edit = taxrate.uuid != null;
    return edit ? this.editTaxRate(taxrate) : this.addTaxRate(taxrate);
  }

  private addTaxRate(taxrate: TaxRate): Observable<TaxRate> {
    let url = this.configService.getUrl() + 'taxrates/';
    type typeToReturn = TaxRate;
    let response = this.commonService.createData<typeToReturn>(url, taxrate);
    return response;
  }

  private editTaxRate(taxrate: TaxRate): Observable<TaxRate> {
    let url = this.configService.getUrl() + 'taxrates/' + taxrate.uuid;
    type typeToReturn = TaxRate;
    let response = this.commonService.editData<typeToReturn>(url, taxrate);
    return response;
  }

  deleteTaxRate(taxrate: TaxRate) {
    let url = this.configService.getUrl() + 'taxrates/' + taxrate.uuid;
    type typeToReturn = TaxRate;
    let response = this.commonService.deleteData<typeToReturn>(url);
    return response;
  }

  getCouponTypes(daily: boolean = null): Observable<CouponType[]> {
    let url = this.configService.getUrl() + 'coupontypes/';
    if (daily != null) {
      url += '?daily=' + daily;
    }
    type typeToReturn = CouponType[];
    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  saveOrUpdateCouponType(coupon: CouponType): Observable<CouponType> {
    let edit = coupon.uuid != null;
    return edit ? this.editCouponType(coupon) : this.addCouponType(coupon);
  }

  private addCouponType(coupon: CouponType): Observable<CouponType> {
    let url = this.configService.getUrl() + 'coupontypes/';
    type typeToReturn = CouponType;
    let response = this.commonService.createData<typeToReturn>(url, coupon);
    return response;
  }

  private editCouponType(coupon: CouponType): Observable<CouponType> {
    let url = this.configService.getUrl() + 'coupontypes/' + coupon.uuid;
    type typeToReturn = CouponType;
    let response = this.commonService.editData<typeToReturn>(url, coupon);
    return response;
  }

  deleteCouponType(coupon: CouponType) {
    let url = this.configService.getUrl() + 'coupontypes/' + coupon.uuid;
    type typeToReturn = CouponType;
    let response = this.commonService.deleteData<typeToReturn>(url);
    return response;
  }

  getUnitsOfMeasure(): Observable<UnitOfMeasure[]> {
    let url = this.configService.getUrl() + 'unitsofmeasure/';
    type typeToReturn = UnitOfMeasure[];
    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  saveOrUpdateUnitOfMeasure(unitOfMeasure: UnitOfMeasure): Observable<UnitOfMeasure> {
    let edit = unitOfMeasure.uuid != null;
    return edit ? this.editUnitOfMeasure(unitOfMeasure) : this.addUnitOfMeasure(unitOfMeasure);
  }

  private addUnitOfMeasure(unitOfMeasure: UnitOfMeasure): Observable<UnitOfMeasure> {
    let url = this.configService.getUrl() + 'unitsofmeasure/';
    type typeToReturn = UnitOfMeasure;
    let response = this.commonService.createData<typeToReturn>(url, unitOfMeasure);
    return response;
  }

  private editUnitOfMeasure(unitOfMeasure: UnitOfMeasure): Observable<UnitOfMeasure> {
    let url = this.configService.getUrl() + 'unitsofmeasure/' + unitOfMeasure.uuid;
    type typeToReturn = UnitOfMeasure;
    let response = this.commonService.editData<typeToReturn>(url, unitOfMeasure);
    return response;
  }

  deleteUnitOfMeasure(unitOfMeasure: UnitOfMeasure) {
    let url = this.configService.getUrl() + 'unitsofmeasure/' + unitOfMeasure.uuid;
    type typeToReturn = UnitOfMeasure;
    let response = this.commonService.deleteData<typeToReturn>(url);
    return response;
  }

  getZones(): Observable<Zone[]> {
    let url = this.configService.getUrl() + 'zones/';
    type typeToReturn = Zone[];
    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  getOrganization(): Observable<Organization> {
    let url = this.configService.getUrl() + 'organizations/organization';
    type typeToReturn = Organization;
    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  saveOrUpdateOrganization(organization: Organization): Observable<Organization> {
    let edit = organization.uuid != null;
    return edit ? this.editOrganization(organization) : this.addOrganization(organization);
  }

  private addOrganization(organization: Organization): Observable<Organization> {
    let url = this.configService.getUrl() + 'organizations/';
    type typeToReturn = Organization;
    let response = this.commonService.createData<typeToReturn>(url, organization);
    return response;
  }

  private editOrganization(organization: Organization): Observable<Organization> {
    let url = this.configService.getUrl() + 'organizations/' + organization.uuid;
    type typeToReturn = Organization;
    let response = this.commonService.editData<typeToReturn>(url, organization);
    return response;
  }

  saveOrUpdateZone(zone: Zone): Observable<Zone> {
    let edit = zone.uuid != null;
    return edit ? this.editZone(zone) : this.addZone(zone);
  }

  private addZone(zone: Zone): Observable<Zone> {
    let url = this.configService.getUrl() + 'zones/';
    type typeToReturn = Zone;
    let response = this.commonService.createData<typeToReturn>(url, zone);
    return response;
  }

  private editZone(zone: Zone): Observable<Zone> {
    let url = this.configService.getUrl() + 'zones/' + zone.uuid;
    type typeToReturn = Zone;
    let response = this.commonService.editData<typeToReturn>(url, zone);
    return response;
  }

  deleteZone(zone: Zone) {
    let url = this.configService.getUrl() + 'zones/' + zone.uuid;
    type typeToReturn = Zone;
    let response = this.commonService.deleteData<typeToReturn>(url);
    return response;
  }

  getPriceLists(): Observable<PriceList[]> {
    let url = this.configService.getUrl() + 'pricelists/';
    type typeToReturn = PriceList[];
    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  saveOrUpdatePriceList(priceList: PriceList): Observable<PriceList> {
    let edit = priceList.uuid != null;
    return edit ? this.editPriceList(priceList) : this.addPriceList(priceList);
  }

  private addPriceList(priceList: PriceList): Observable<PriceList> {
    let url = this.configService.getUrl() + 'pricelists/';
    type typeToReturn = PriceList;
    let response = this.commonService.createData<typeToReturn>(url, priceList);
    return response;
  }

  private editPriceList(priceList: PriceList): Observable<PriceList> {
    let url = this.configService.getUrl() + 'pricelists/' + priceList.uuid;
    type typeToReturn = PriceList;
    let response = this.commonService.editData<typeToReturn>(url, priceList);
    return response;
  }

  deletePriceList(priceList: PriceList) {
    let url = this.configService.getUrl() + 'pricelists/' + priceList.uuid;
    type typeToReturn = PriceList;
    let response = this.commonService.deleteData<typeToReturn>(url);
    return response;
  }

  saveOrUpdatePriceListProduct(priceListProduct: PricelistProduct): Observable<PricelistProduct> {
    let edit = priceListProduct.uuid != null;
    return edit ? this.editPriceListProduct(priceListProduct) : this.addPriceListProduct(priceListProduct);
  }

  private addPriceListProduct(priceListProduct: PricelistProduct): Observable<PricelistProduct> {
    let url = this.configService.getUrl() + 'pricelistproducts/';
    type typeToReturn = PricelistProduct;
    let response = this.commonService.createData<typeToReturn>(url, priceListProduct);
    return response;
  }

  private editPriceListProduct(priceListProduct: PricelistProduct): Observable<PricelistProduct> {
    let url = this.configService.getUrl() + 'pricelistproducts/' + priceListProduct.uuid;
    type typeToReturn = PricelistProduct;
    let response = this.commonService.editData<typeToReturn>(url, priceListProduct);
    return response;
  }

  deletePriceListProduct(priceListProduct: PricelistProduct) {
    let url = this.configService.getUrl() + 'pricelistproducts/' + priceListProduct.uuid;
    type typeToReturn = PricelistProduct;
    let response = this.commonService.deleteData<typeToReturn>(url);
    return response;
  }

  getPriceListsProductByPos(pos: PoS): Observable<PricelistProduct[]> {
    let url = this.configService.getUrl() + 'pointsofsale/' + pos.uuid + '/pricelistproducts';
    type typeToReturn = PricelistProduct[];
    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  getPriceListsByProduct(product: Product): Observable<PricelistProduct[]> {
    let url = this.configService.getUrl() + 'products/' + product.uuid + '/pricelistproducts';
    type typeToReturn = PricelistProduct[];
    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  saveOrUpdateProductCategory(category: ProductCategory): Observable<ProductCategory> {
    let edit = category.uuid != null;
    return edit ? this.editProductCategory(category) : this.addProductCategory(category);
  }

  private addProductCategory(category: ProductCategory): Observable<ProductCategory> {
    let url = this.configService.getUrl() + 'productcategories/';
    type typeToReturn = ProductCategory;
    let response = this.commonService.createData<typeToReturn>(url, category);
    return response;
  }

  private editProductCategory(category: ProductCategory): Observable<ProductCategory> {
    let url = this.configService.getUrl() + 'productcategories/' + category.uuid;
    type typeToReturn = ProductCategory;
    let response = this.commonService.editData<typeToReturn>(url, category);
    return response;
  }

  deleteProductCategory(category: ProductCategory) {
    let url = this.configService.getUrl() + 'productcategories/' + category.uuid;
    type typeToReturn = ProductCategory;
    let response = this.commonService.deleteData<typeToReturn>(url);
    return response;
  }

  getProductCategories(): Observable<ProductCategory[]> {
    let url = this.configService.getUrl() + 'productcategories/';
    type typeToReturn = ProductCategory[];
    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  getProductCategory(codeId: string): Observable<ProductCategory> {
    let url = this.configService.getUrl() + 'productcategories/code/' + codeId;
    type typeToReturn = ProductCategory;
    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  getProductsByCategory(category: ProductCategory) {
    let url = this.configService.getUrl() + 'productcategories/' + category.uuid + '/products';
    type typeToReturn = ProductCategory;
    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  getSecurityDepositsByCard(card: Card): Observable<CardTransaction[]> {
    let url = this.configService.getUrl() + 'cards/' + card.uuid + '/securitydeposits';
    type typeToReturn = CardTransaction[];
    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  getSecurityDepositsByCurrentAccount(currentAccount: CurrentAccount): Observable<CardTransaction[]> {
    let url = this.configService.getUrl() + 'currentaccounts/' + currentAccount.uuid + '/securitydeposits';
    type typeToReturn = CardTransaction[];
    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  addDailyCouponList(dailycouponlist: DailyCouponList): Observable<Coupon[]> {
    let url = this.configService.getUrl() + 'coupons/list';
    type typeToReturn = Coupon[];
    let response = this.commonService.createData<typeToReturn>(url, dailycouponlist);
    return response;
  }

  saveOrUpdateCoupon(coupon: Coupon): Observable<Coupon> {
    let edit = coupon.uuid != null;
    return edit ? this.editCoupon(coupon) : this.addCoupon(coupon);
  }

  private addCoupon(coupon: Coupon): Observable<Coupon> {
    let url = this.configService.getUrl() + 'coupons/';
    type typeToReturn = Coupon;
    let response = this.commonService.createData<typeToReturn>(url, coupon);
    return response;
  }

  private editCoupon(coupon: Coupon): Observable<Coupon> {
    let url = this.configService.getUrl() + 'coupons/' + coupon.uuid;
    type typeToReturn = Coupon;
    let response = this.commonService.editData<typeToReturn>(url, coupon);
    return response;
  }

  getCouponByCard(card: Card): Observable<Coupon[]> {
    let url = this.configService.getUrl() + 'cards/' + card.uuid + '/coupons';
    type typeToReturn = Coupon[];
    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  addCouponRechargeTransaction(transaction: CouponTransaction): Observable<CouponTransaction> {
    let url = this.configService.getUrl() + 'coupontransactions/recharges';
    type typeToReturn = CouponTransaction;
    let response = this.commonService.createData<typeToReturn>(url, transaction);
    return response;
  }

  addCouponPaymentTransaction(transaction: CouponTransaction): Observable<CouponTransaction> {
    let url = this.configService.getUrl() + 'coupontransactions/payments';
    type typeToReturn = CouponTransaction;
    let response = this.commonService.createData<typeToReturn>(url, transaction);
    return response;
  }

  addCouponDischargeTransaction(transaction: CouponTransaction): Observable<CouponTransaction> {
    let url = this.configService.getUrl() + 'coupontransactions/discharges';
    type typeToReturn = CouponTransaction;
    let response = this.commonService.createData<typeToReturn>(url, transaction);
    return response;
  }

  getCardStatementPdf(cardUuid: string): Observable<Blob> {
    let url = this.configService.getUrl() + 'cards/' + cardUuid + '/statements/pdf';
    let response = this.getPDF(url);
    return response;
  }

  getCardTransactionReceiptPdf(cardTransaction: CardTransaction): Observable<Blob> {
    let url = this.configService.getUrl() + 'cardtransactions/' + cardTransaction.uuid + '/receipt/pdf';
    let response = this.getPDF(url);
    return response;
  }

  getRegisterActivityPdf(activityUuid: string): Observable<Blob> {
    let url = this.configService.getUrl() + 'posregisteractivities/' + activityUuid + '/pdf';
    let response = this.getPDF(url);
    return response;
  }

  getRegisterActivitiesPdf(
    posUuid: string,
    zoneUuid: string,
    cardUuid: string,
    cardTypeUuid: string,
    userUuid: string,
    customerUuid: string,
    includePaymentsCreditCard: boolean,
    includePaymentsDebitCard: boolean,
    includeRechargesAndDischarges: boolean,
    includeRegisterActivities: boolean,
    reversed: boolean,
    startDate: Date,
    endDate: Date
  ): Observable<Blob> {
    let url = this.configService.getUrl() + 'posregisteractivities/pdf';
    const queryParamsString: string = this.createPdfFiltersQueryParamsString(
      posUuid,
      zoneUuid,
      cardUuid,
      cardTypeUuid,
      userUuid,
      customerUuid,
      includePaymentsCreditCard,
      includePaymentsDebitCard,
      includeRechargesAndDischarges,
      includeRegisterActivities,
      reversed,
      startDate,
      endDate
    );
    if (queryParamsString.length) {
      url += '?' + queryParamsString;
    }
    let response = this.getPDF(url);
    return response;
  }

  getCashBalancePdf(startDate: Date, endDate: Date): Observable<Blob> {
    let url = this.configService.getUrl() + 'cashbalance/pdf';
    const queryParamsString: string = this.createPdfFiltersQueryParamsString(
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      startDate,
      endDate
    );
    if (queryParamsString.length) {
      url += '?' + queryParamsString;
    }
    let response = this.getPDF(url);
    return response;
  }

  getCurrentAccountPdf(currentAccountUuid: string): Observable<Blob> {
    let url = this.configService.getUrl() + 'currentaccounts/' + currentAccountUuid + '/pdf';
    let response = this.getPDF(url);
    return response;
  }

  getPosTransactionsStatementPdf(posUuid: string, fromDate?: string, toDate?: string): Observable<Blob> {
    let url = this.configService.getUrl() + 'pointsofsale/' + posUuid + '/cardtransactions/pdf';
    let response = this.getPDF(url, { startDate: fromDate, endDate: toDate });
    return response;
  }

  getPosStatementPdf(
    posUuid: string,
    zoneUuid: string,
    cardUuid: string,
    cardTypeUuid: string,
    userUuid: string,
    customerUuid: string,
    includePaymentsCreditCard: boolean,
    includePaymentsDebitCard: boolean,
    includeRechargesAndDischarges: boolean,
    includeRegisterActivities: boolean,
    reversed: boolean,
    startDate: Date,
    endDate: Date
  ): Observable<Blob> {
    let url = this.configService.getUrl() + 'pointsofsale/pdf';
    const queryParamsString: string = this.createPdfFiltersQueryParamsString(
      posUuid,
      zoneUuid,
      cardUuid,
      cardTypeUuid,
      userUuid,
      customerUuid,
      includePaymentsCreditCard,
      includePaymentsDebitCard,
      includeRechargesAndDischarges,
      includeRegisterActivities,
      reversed,
      startDate,
      endDate
    );
    if (queryParamsString.length) {
      url += '?' + queryParamsString;
    }
    let response = this.getPDF(url);
    return response;
  }

  getZoneStatementPdf(
    posUuid: string,
    zoneUuid: string,
    cardUuid: string,
    cardTypeUuid: string,
    userUuid: string,
    customerUuid: string,
    includePaymentsCreditCard: boolean,
    includePaymentsDebitCard: boolean,
    includeRechargesAndDischarges: boolean,
    includeRegisterActivities: boolean,
    reversed: boolean,
    startDate: Date,
    endDate: Date
  ): Observable<Blob> {
    let url = this.configService.getUrl() + 'zones/pdf';
    const queryParamsString: string = this.createPdfFiltersQueryParamsString(
      posUuid,
      zoneUuid,
      cardUuid,
      cardTypeUuid,
      userUuid,
      customerUuid,
      includePaymentsCreditCard,
      includePaymentsDebitCard,
      includeRechargesAndDischarges,
      includeRegisterActivities,
      reversed,
      startDate,
      endDate
    );
    if (queryParamsString.length) {
      url += '?' + queryParamsString;
    }
    let response = this.getPDF(url);
    return response;
  }

  private createPdfFiltersQueryParamsString(
    posUuid: string,
    zoneUuid: string,
    cardUuid: string,
    cardTypeUuid: string,
    userUuid: string,
    customerUuid: string,
    includePaymentsCreditCard: boolean,
    includePaymentsDebitCard: boolean,
    includeRechargesAndDischarges: boolean,
    includeRegisterActivities: boolean,
    reversed: boolean,
    startDate: Date,
    endDate: Date
  ): string {
    const queryParamsMap = {};
    posUuid && (queryParamsMap['posUuid'] = posUuid);
    zoneUuid && (queryParamsMap['zoneUuid'] = zoneUuid);
    cardUuid && (queryParamsMap['cardUuid'] = cardUuid);
    cardTypeUuid && (queryParamsMap['cardTypeUuid'] = cardTypeUuid);
    userUuid && (queryParamsMap['userUuid'] = userUuid);
    customerUuid && (queryParamsMap['customerUuid'] = customerUuid);
    reversed && (queryParamsMap['reversed'] = reversed);
    (includePaymentsCreditCard || includePaymentsCreditCard === false) &&
      (queryParamsMap['includePaymentsCreditCard'] = includePaymentsCreditCard);
    (includePaymentsDebitCard || includePaymentsDebitCard === false) &&
      (queryParamsMap['includePaymentsDebitCard'] = includePaymentsDebitCard);
    (includeRechargesAndDischarges || includeRechargesAndDischarges === false) &&
      (queryParamsMap['includeRechargesAndDischarges'] = includeRechargesAndDischarges);
    (includeRegisterActivities || includeRegisterActivities === false) &&
      (queryParamsMap['includeRegisterActivities'] = includeRegisterActivities);
    startDate && (queryParamsMap['startDate'] = this.toPdfQueryParamDate(startDate));
    endDate && (queryParamsMap['endDate'] = this.toPdfQueryParamDate(endDate));
    const queryParamsTokens: string[] = [];
    for (const paramName in queryParamsMap) {
      queryParamsTokens.push(paramName + '=' + queryParamsMap[paramName]);
    }
    return queryParamsTokens.join('&');
  }

  private toPdfQueryParamDate(date: Date): string {
    return (
      date.getFullYear() + '-' + ('0' + (date.getMonth() + 1)).substr(-2) + '-' + ('0' + date.getDate()).substr(-2)
    );
  }

  getAllPosStatementPdf(fromDate?: string, toDate?: string): Observable<Blob> {
    let url = this.configService.getUrl() + 'pointsofsale/pdf';
    let response = this.getPDF(url, { startDate: fromDate, endDate: toDate });
    return response;
  }

  addFile(data: FormData): Observable<FileModel> {
    let url = this.configService.getUrl() + 'uploadfiles/';
    type typeToReturn = FileModel;
    let response = this.postFile<typeToReturn>(url, data);
    return response;
  }

  getLastCardTransactionByCard(card: Card): Observable<CardTransaction> {
    let url = this.configService.getUrl() + 'cards/' + card.uuid + '/lasttransaction';
    type typeToReturn = CardTransaction;
    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  private postFile<T>(url: string, data: object): Observable<T> {
    let response = this.http.post<T>(url, data, {
      reportProgress: true,
      observe: 'body',
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authenticationService.token,
      }),
    });
    return response;
  }

  private getPDF(url: string, params?: any): Observable<Blob> {
    let observable = this.http.get<Blob>(url, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authenticationService.token,
      }),
      params: params,
      responseType: 'blob' as 'json',
    });
    return observable;
  }

  getPosAppVersion(): Observable<PosAppVersion> {
    let url = this.configService.getUrl() + 'posappversion';
    type typeToReturn = PosAppVersion;
    let response = this.commonService.getData<typeToReturn>(url);
    return response;
  }

  saveOrUpdatePosAppVersion(posAppVersion: PosAppVersion): Observable<PosAppVersion> {
    let edit = posAppVersion.uuid != null;
    return edit ? this.editPosAppVersion(posAppVersion) : this.addPosAppVersion(posAppVersion);
  }

  private addPosAppVersion(posAppVersion: PosAppVersion): Observable<PosAppVersion> {
    let url = this.configService.getUrl() + 'posappversions/';
    type typeToReturn = PosAppVersion;
    let response = this.commonService.createData<typeToReturn>(url, posAppVersion);
    return response;
  }

  private editPosAppVersion(posAppVersion: PosAppVersion): Observable<PosAppVersion> {
    let url = this.configService.getUrl() + 'posappversions/' + posAppVersion.uuid;
    type typeToReturn = PosAppVersion;
    let response = this.commonService.editData<typeToReturn>(url, posAppVersion);
    return response;
  }

  ping(): Observable<string> {
    let url = this.configService.getUrl() + 'ping';
    let response = this.http.get(url, { responseType: 'text' });
    return response;
  }

  dumpdb(): Observable<HttpResponse<Blob>> {
    let url = this.configService.getUrl() + 'dumpdb';
    let response = this.http.get(url, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authenticationService.token,
      }),
      observe: 'response',
      responseType: 'blob'
    });
    return response;
  }
}



// WEBPACK FOOTER //
// ./src/providers/paytron-backend/paytron-backend.service.ts